import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Home, KeyRound, Loader2, ArrowLeft, Clock, UserCircle } from "lucide-react";
import { PlanSelector } from "../components/PlanSelector";
import { MediaUploader } from "../components/MediaUploader";
import { LocationAutocomplete } from "../components/LocationAutocomplete";

const inputCls = "w-full mt-1 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]";
const inputStyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" };
const ONBOARD_BG = "https://images.unsplash.com/photo-1536138746221-2c489206ff6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGx1eHVyeSUyMHRleHR1cmV8ZW58MHx8fGJsYWNrfDE3ODY4Mjk4MDh8MA&ixlib=rb-4.1.0&q=85";

export default function OnboardingPage() {
  const { refreshUser, user, logout } = useAuth();
  const [tab, setTab] = useState("choose"); // choose | create | join | profile | plan
  const [flow, setFlow] = useState(null); // create | join
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [inviteFamily, setInviteFamily] = useState(null);
  const [profile, setProfile] = useState({ photo: "", birthday: "", current_city: "", bio: "" });
  const [landing, setLanding] = useState(null);
  const [duesAgreed, setDuesAgreed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/families/pending");
        if (data.length) setPending(data[0].family_name);
      } catch {}
      const inv = localStorage.getItem("fv_invite");
      if (inv) {
        setCode(inv);
        setFlow("join");
        try { const { data } = await api.get(`/families/landing/${inv}`); setInviteFamily(data.family_name); setLanding(data); } catch {}
        setTab("profile"); // invited users set up profile first
      }
    })();
  }, []);

  const doCreateWithPlan = async (tier, billing) => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      await api.post("/families", { name: name.trim(), tier, billing });
      await refreshUser();
      if (tier === "roots") {
        toast.success("Family created — you are the Admin");
        navigate("/lineage");
      } else {
        // Paid tier: refreshUser re-renders the app into the subscription gate,
        // which charges before granting access. Never auto-enroll without payment.
        toast.success("Family created — activate your plan to enter");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/profile", {
        photo: profile.photo || null, current_city: profile.current_city || null,
        bio: profile.bio || null, birthday: profile.birthday || null,
      });
      if (landing && (landing.dues_amount || 0) > 0) setTab("dues");
      else setTab("plan");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doJoinWithPlan = async (tier, billing) => {
    setLoading(true);
    try {
      const { data } = await api.post("/families/join", { invite_code: code.trim(), tier, billing, dues_agreed: duesAgreed });
      localStorage.removeItem("fv_invite");
      if (data.status === "joined") {
        await refreshUser();
        toast.success("Joined the family");
        navigate("/lineage");
      } else {
        setPending(data.family?.name || inviteFamily || "your family");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  if (pending) {
    return (
      <div className="min-h-screen w-full grain grid place-items-center text-center px-7 py-14" style={{ background: "var(--bg-base)" }} data-testid="join-pending-screen">
        <div>
          <Clock size={44} className="gold-text mx-auto" />
          <h1 className="font-serif-display text-3xl mt-4">Almost there</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
            Your request to join <b>{pending}</b> is waiting for an admin or organizer to approve you. You'll get access — and only then be charged for your plan — the moment they accept.
          </p>
          <button onClick={logout} className="mt-8 text-sm gold-text" data-testid="pending-logout-btn">Log out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full grain relative" style={{ background: "var(--bg-base)" }} data-testid="onboarding-screen">
      <div className="lg:grid lg:grid-cols-2 min-h-screen">
        <div className="hidden lg:block relative overflow-hidden">
          <img src={ONBOARD_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(11,11,18,0.9), rgba(11,11,18,0.6))" }} />
          <div className="relative z-10 h-full flex flex-col justify-between p-14">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl grid place-items-center gold-border" style={{ background: "rgba(20,20,31,0.6)" }}>
                <Home size={22} className="gold-text" />
              </div>
              <span className="font-serif-display text-2xl">The Family Vault</span>
            </div>
            <div>
              <h2 className="font-serif-display text-5xl leading-[1.05] max-w-md">Every family deserves a private place to call home.</h2>
              <p className="mt-5 text-base max-w-sm" style={{ color: "var(--text-secondary)" }}>Set up your vault in just a couple of minutes.</p>
            </div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>© {new Date().getFullYear()} The Family Vault</div>
          </div>
        </div>
        <div className="relative z-10 flex items-start lg:items-center justify-center min-h-screen">
          <div className="w-full max-w-md px-7 py-14">
      {(tab === "create" || tab === "join") && (
        <button onClick={() => setTab("choose")} className="mb-6 flex items-center gap-1 text-sm" style={{ color: "var(--text-secondary)" }} data-testid="onboarding-back-btn">
          <ArrowLeft size={16} /> Back
        </button>
      )}

      {tab === "choose" && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Welcome, {user?.name?.split(" ")[0]}</p>
          <h1 className="font-serif-display text-4xl mt-1">Let's set up<br />your family space</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
            Start a new private vault for your family, or join an existing one with an invite code.
          </p>
          <div className="mt-9 space-y-4">
            <motion.button whileTap={{ scale: 0.98 }} onClick={() => { setFlow("create"); setTab("create"); }} data-testid="choose-create-btn"
              className="w-full card-surface rounded-2xl p-5 text-left flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl grid place-items-center gold-border" style={{ background: "var(--bg-elevated)" }}>
                <Home size={20} className="gold-text" />
              </div>
              <div>
                <div className="font-semibold">Create a Family</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>You become the first Admin and invite others.</div>
              </div>
            </motion.button>
            <motion.button whileTap={{ scale: 0.98 }} onClick={() => { setFlow("join"); setTab("join"); }} data-testid="choose-join-btn"
              className="w-full card-surface rounded-2xl p-5 text-left flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl grid place-items-center gold-border" style={{ background: "var(--bg-elevated)" }}>
                <KeyRound size={20} className="gold-text" />
              </div>
              <div>
                <div className="font-semibold">Join a Family</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>Enter an invite code shared with you.</div>
              </div>
            </motion.button>
          </div>
        </motion.div>
      )}

      {tab === "create" && (
        <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="font-serif-display text-4xl">Name your family</h1>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="create-family-input"
            placeholder="e.g. The Okoye Family" className={inputCls} style={inputStyle} />
          <motion.button whileTap={{ scale: 0.97 }} onClick={() => { if (name.trim()) setTab("plan"); }} disabled={loading} data-testid="create-family-submit"
            className="w-full mt-4 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
            style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
            Continue to plans
          </motion.button>
        </motion.div>
      )}

      {tab === "join" && (
        <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}>
          <h1 className="font-serif-display text-4xl">Enter invite code</h1>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="join-family-input"
            placeholder="e.g. A1B2C3D4"
            className="w-full mt-6 rounded-xl px-4 py-3.5 text-sm outline-none tracking-widest font-serif-display text-xl focus:ring-2 focus:ring-[var(--accent)]"
            style={inputStyle} />
          <motion.button whileTap={{ scale: 0.97 }} onClick={() => { if (code.trim()) setTab("profile"); }} disabled={loading} data-testid="join-family-submit"
            className="w-full mt-4 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
            style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
            Continue
          </motion.button>
        </motion.div>
      )}

      {tab === "profile" && (
        <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} data-testid="onboarding-profile">
          {inviteFamily && <p className="text-sm gold-text">Joining {inviteFamily}</p>}
          <h1 className="font-serif-display text-4xl mt-1">Set up<br />your profile</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>Tell your family a little about you.</p>

          <div className="mt-6 flex items-center gap-4">
            {profile.photo ? (
              <img src={fileUrl(profile.photo)} alt="" className="h-16 w-16 rounded-full object-cover gold-border" />
            ) : (
              <div className="h-16 w-16 rounded-full grid place-items-center" style={{ background: "var(--overlay)" }}><UserCircle size={30} className="gold-text" /></div>
            )}
            <MediaUploader label="Add photo" testid="onboarding-photo-upload" onUploaded={(p) => setProfile((s) => ({ ...s, photo: p }))} />
          </div>

          <div className="mt-4 space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Birthday</label>
              <input type="date" value={profile.birthday} onChange={(e) => setProfile({ ...profile, birthday: e.target.value })}
                data-testid="onboarding-birthday" className={inputCls} style={inputStyle} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Current city</label>
              <LocationAutocomplete value={profile.current_city} onChange={(v) => setProfile((s) => ({ ...s, current_city: v }))} testid="onboarding-city" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>About you</label>
              <textarea rows={3} value={profile.bio} onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                placeholder="A short bio…" data-testid="onboarding-bio" className={inputCls} style={inputStyle} />
            </div>
          </div>

          <motion.button whileTap={{ scale: 0.97 }} onClick={saveProfile} disabled={loading} data-testid="onboarding-profile-continue"
            className="w-full mt-5 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
            style={{ background: "var(--accent)", color: "#fff" }}>
            {loading && <Loader2 size={16} className="animate-spin" />} Continue to plans
          </motion.button>
        </motion.div>
      )}

      {tab === "dues" && landing && (
        <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} data-testid="onboarding-dues">
          {inviteFamily && <p className="text-sm gold-text">Joining {inviteFamily}</p>}
          <h1 className="font-serif-display text-4xl mt-1">Family dues</h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-secondary)" }}>
            {inviteFamily} collects dues from members to support the family.
          </p>
          <div className="card-surface rounded-2xl p-5 mt-5 text-center">
            <div className="text-[11px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Dues amount</div>
            <div className="font-serif-display text-4xl gold-text mt-1" data-testid="dues-amount">${Number(landing.dues_amount).toFixed(2)}</div>
            <div className="text-xs mt-1 capitalize" style={{ color: "var(--text-secondary)" }}>
              billed {landing.dues_frequency} · on the {landing.dues_day}
              {landing.dues_day === 1 ? "st" : landing.dues_day === 30 ? "th" : "th"} of the period
            </div>
          </div>
          <label className="flex items-start gap-3 mt-5 text-sm cursor-pointer" style={{ color: "var(--text-secondary)" }}>
            <input type="checkbox" checked={duesAgreed} onChange={(e) => setDuesAgreed(e.target.checked)}
              data-testid="dues-agree-checkbox" className="h-5 w-5 mt-0.5 accent-[var(--accent)]" />
            I agree to pay {inviteFamily}'s dues of ${Number(landing.dues_amount).toFixed(2)} {landing.dues_frequency}. I won't be charged until an admin admits me.
          </label>
          <motion.button whileTap={{ scale: 0.97 }} onClick={() => setTab("plan")} disabled={!duesAgreed}
            data-testid="dues-continue-btn"
            className="w-full mt-5 rounded-xl py-3.5 font-semibold text-sm disabled:opacity-50"
            style={{ background: "var(--accent)", color: "#fff" }}>
            Agree & continue
          </motion.button>
        </motion.div>
      )}

      {tab === "plan" && (
        <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} data-testid="onboarding-plan">
          {flow === "create" && (
            <button onClick={() => setTab("create")} className="mb-4 flex items-center gap-1 text-sm" style={{ color: "var(--text-secondary)" }} data-testid="plan-back-btn">
              <ArrowLeft size={16} /> Back
            </button>
          )}
          <h1 className="font-serif-display text-4xl">Choose your plan</h1>
          <p className="mt-2 mb-5 text-sm" style={{ color: "var(--text-secondary)" }}>
            {flow === "create"
              ? "Pick a plan for your new family. Paid plans are activated after checkout — you're never charged without confirming."
              : "Pick a plan to request access. You won't be charged until an admin admits you."}
          </p>
          <PlanSelector
            onSelect={flow === "create" ? doCreateWithPlan : doJoinWithPlan}
            submitting={loading}
            ctaLabel={flow === "create" ? "Continue" : "Request to join"}
          />
        </motion.div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
}
